import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import type { PilotRelationshipAccessType } from '@shared';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  Calendar,
  Card,
  CardContent,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Empty,
  EmptyDescription,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemSeparator,
  ItemTitle,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@design-system';
import {
  ArrowLeft,
  CalendarIcon,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  Clock3,
  Ellipsis,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Send,
  Trash2,
} from 'lucide-react';

import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog';
import { toDateOnlyString } from '@/shared/lib/utils';
import { getErrorMessage } from '@/features/wizard-advisor-admin/utils/get-error-message';
import {
  type PilotOnboardingRecord,
  type PilotProgram,
  type PilotRelationshipIdentity,
} from './pilot-onboarding-api';
import {
  dateTimeValue,
  formatPilotDate as formatDate,
  getPilotOnboardingActions,
  pilotAdvisorName as advisorName,
  pilotAdvisorNameOrEmail as advisorNameOrEmail,
  usePilotOnboardingAdminController,
  type PilotOnboardingStatusFilter,
} from './use-pilot-onboarding-admin';
import { PilotPartnerSettings } from './PilotPartnerSettings';

function formatInvitedDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function firstNameFromEmail(value: string): string {
  const first = value.split('@')[0].replace(/[+].*/, '').split(/[._-]/).filter(Boolean)[0];
  return first ? `${first.charAt(0).toUpperCase()}${first.slice(1)}` : 'This user';
}

function statusPresentation(record: PilotOnboardingRecord): {
  label: string;
  variant: 'default' | 'secondary' | 'destructive' | 'success' | 'warning';
  dotClassName: string;
} {
  if (record.status === 'active') {
    return {
      label: 'Active',
      variant: 'success',
      dotClassName: 'bg-green-500',
    };
  }
  if (record.status === 'invited') {
    return record.advisorId
      ? {
          label: 'Seat pending',
          variant: 'default',
          dotClassName: 'bg-blue-500',
        }
      : {
          label: 'Awaiting signup',
          variant: 'secondary',
          dotClassName: 'bg-gray-400',
        };
  }
  if (record.status === 'needs_attention') {
    return {
      label: 'Step failed',
      variant: 'destructive',
      dotClassName: 'bg-red-500',
    };
  }
  if (record.status === 'declined') {
    return {
      label: 'Invitation declined',
      variant: 'warning',
      dotClassName: 'bg-amber-500',
    };
  }
  if (record.status === 'expired') {
    return {
      label: 'Invitation expired',
      variant: 'destructive',
      dotClassName: 'bg-red-500',
    };
  }
  if (record.status === 'processing') {
    return {
      label: 'Setting up',
      variant: 'default',
      dotClassName: 'bg-blue-500',
    };
  }
  return {
    label: record.status === 'deleted' ? 'Deleted' : 'Queued',
    variant: 'secondary',
    dotClassName: 'bg-gray-400',
  };
}

function StatusBadge({ record }: { record: PilotOnboardingRecord }) {
  const badge = statusPresentation(record);
  return (
    <Badge variant={badge.variant}>
      <span className={`size-1.5 rounded-full ${badge.dotClassName}`} />
      {badge.label}
    </Badge>
  );
}

function EmptyState({ children }: { children: string }) {
  return (
    <Empty className="border py-8 md:p-8">
      <EmptyDescription>{children}</EmptyDescription>
    </Empty>
  );
}

function SectionNumber({ children }: { children: string }) {
  return (
    <span className="bg-primary/15 text-primary-dark inline-flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold">
      {children}
    </span>
  );
}

function SectionHeading({
  number,
  title,
  action,
}: {
  number: string;
  title: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex min-h-8 items-center gap-2">
      <SectionNumber>{number}</SectionNumber>
      <h3 className="text-foreground text-sm font-semibold">{title}</h3>
      <div className="ml-auto">{action}</div>
    </div>
  );
}

function accessPartyName(value: string): string {
  const firstPart = value.includes('@')
    ? value.split('@')[0].split(/[.+_-]/)[0]
    : value.trim().split(/\s+/)[0];
  return firstPart ? `${firstPart[0].toUpperCase()}${firstPart.slice(1)}` : 'This user';
}

function relationshipAccessOptions(relatedUserName: string) {
  const relatedName = accessPartyName(relatedUserName);
  return [
    { value: 'no_client_access' as const, label: 'No client access' },
    { value: 'two_way' as const, label: 'See each other’s clients' },
    {
      value: 'new_user_sees_related_clients' as const,
      label: `This user sees ${relatedName}’s clients`,
    },
    {
      value: 'related_user_sees_new_user_clients' as const,
      label: `${relatedName} sees this user’s clients`,
    },
  ];
}

function accessLabel(accessType: PilotRelationshipAccessType, relatedUserName: string): string {
  return (
    relationshipAccessOptions(relatedUserName).find(option => option.value === accessType)?.label ??
    accessType
  );
}

function AccessSelect({
  value,
  onChange,
  ariaLabel,
  relatedUserName,
}: {
  value: PilotRelationshipAccessType;
  onChange: (value: PilotRelationshipAccessType) => void;
  ariaLabel: string;
  relatedUserName: string;
}) {
  const accessOptions = relationshipAccessOptions(relatedUserName);
  return (
    <Select
      value={value}
      onValueChange={nextValue => onChange(nextValue as PilotRelationshipAccessType)}
    >
      <SelectTrigger
        aria-label={ariaLabel}
        size="sm"
        className="min-w-52 bg-white"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {accessOptions.map(option => (
          <SelectItem
            key={option.value}
            value={option.value}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function reviewEmailInitials(email: string): string {
  const localParts = email.split('@')[0].replace(/[+].*/, '').split(/[._-]/).filter(Boolean);
  return (
    localParts.length > 1
      ? `${localParts[0][0]}${localParts[1][0]}`
      : (localParts[0] || '?').slice(0, 2)
  ).toUpperCase();
}

function ReviewRelationshipItem({
  advisor,
  email,
  accessType,
}: {
  advisor: PilotRelationshipIdentity | null | undefined;
  email: string;
  accessType: PilotRelationshipAccessType;
}) {
  const name = advisorNameOrEmail(advisor, email);

  return (
    <Item
      size="sm"
      className="rounded-none px-3 py-2"
    >
      <ItemContent className="min-w-0 gap-0.5">
        <ItemTitle className="truncate text-[13px]">{name}</ItemTitle>
        {name !== email && <ItemDescription className="truncate text-xs">{email}</ItemDescription>}
      </ItemContent>
      <ItemActions className="ml-auto max-w-[48%]">
        <Badge variant={accessType === 'no_client_access' ? 'secondary' : 'default'}>
          {accessLabel(accessType, name)}
        </Badge>
      </ItemActions>
    </Item>
  );
}

function LoadingState() {
  return (
    <div className="text-muted-foreground flex min-h-64 items-center justify-center gap-2 text-sm">
      <Spinner />
      Loading pilot onboarding…
    </div>
  );
}

function SubmittingInvitationDetails({
  program,
  roleLabel,
  email,
  supervisorCount,
  staffCount,
  advisorExists,
  seatExpiresDateTime,
}: {
  program: PilotProgram;
  roleLabel: string;
  email: string;
  supervisorCount: number;
  staffCount: number;
  advisorExists: boolean;
  seatExpiresDateTime: string | null;
}) {
  const setupSteps = [
    {
      key: 'flags',
      title: `Apply ${program.shortName} pre-registered feature flags`,
    },
    { key: 'relationships', title: 'Team access' },
    { key: 'seat_invitation', title: 'Sponsored-seat invitation' },
  ] as const;

  return (
    <>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <span className="text-muted-foreground inline-flex items-center gap-1">
              <ArrowLeft className="size-4" />
              Pilot invitations
            </span>
          </BreadcrumbItem>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <BreadcrumbItem className="min-w-0">
            <BreadcrumbPage className="truncate">{email}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <Card className="rounded-[14px] border shadow-sm">
        <CardContent className="p-6">
          <div className="grid gap-1.5">
            <p className="text-muted-foreground text-xs">
              {program.displayName} pilot · {roleLabel}
            </p>
            <h2 className="text-foreground break-all text-lg font-semibold">{email}</h2>
            <div className="mt-0.5 flex flex-wrap gap-2">
              <Badge variant="default">
                <span className="size-1.5 animate-pulse rounded-full bg-blue-500" />
                Setting up
              </Badge>
            </div>
          </div>

          <div className="relative mt-5 h-1 overflow-hidden rounded-full bg-gray-100">
            <motion.div
              className="bg-primary absolute inset-y-0 left-0 rounded-full"
              initial={{ width: '8%' }}
              animate={{ width: '88%' }}
              transition={{ duration: 8, ease: [0.16, 1, 0.3, 1] }}
            />
          </div>

          <div className="mt-3 divide-y divide-gray-100 border-t border-gray-100">
            {setupSteps.map((step, index) => (
              <motion.div
                key={step.key}
                className="flex gap-3.5 py-3.5"
                animate={{ opacity: [0.55, 1, 0.55] }}
                transition={{
                  duration: 1.8,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: index * 0.3,
                }}
              >
                <Spinner className="text-primary mt-0.5 size-5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-foreground text-sm font-medium">{step.title}</p>
                  <p className="text-muted-foreground mt-0.5 text-[13px] leading-[19px]">
                    Working…
                  </p>
                  {step.key === 'relationships' && (
                    <p className="text-muted-foreground mt-1 text-[13px]">
                      {supervisorCount} supervisors · {staffCount} team{' '}
                      {staffCount === 1 ? 'member' : 'members'}
                    </p>
                  )}
                  {step.key === 'seat_invitation' && seatExpiresDateTime && (
                    <p className="text-muted-foreground mt-2 text-[13px]">
                      Seat expires{' '}
                      <span className="text-foreground ml-1 font-medium">
                        {formatDate(seatExpiresDateTime)}
                      </span>
                    </p>
                  )}
                </div>
              </motion.div>
            ))}

            <div className="flex gap-3.5 py-3.5">
              {advisorExists ? (
                <CircleCheck className="mt-0.5 size-5 shrink-0 text-green-600" />
              ) : (
                <Clock3 className="text-muted-foreground mt-0.5 size-5 shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-foreground text-sm font-medium">Account created</p>
                <p className="text-muted-foreground mt-0.5 text-[13px] leading-[19px]">
                  {advisorExists ? 'Signed up.' : 'Waiting for the user to sign up.'}
                </p>
              </div>
            </div>

            <div className="flex gap-3.5 py-3.5">
              <Clock3 className="text-muted-foreground mt-0.5 size-5 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-foreground text-sm font-medium">Seat accepted</p>
                <p className="text-muted-foreground mt-0.5 text-[13px] leading-[19px]">
                  Waiting for the user to accept the seat.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

export function PilotOnboardingAdmin() {
  const {
    programsQuery,
    onboardingsQuery,
    programs,
    normalizedEmail,
    validEmail,
    currentPreflight,
    staffSearchQuery,
    submitMutation,
    retryMutation,
    resendMutation,
    deleteMutation,
    view,
    setView,
    editing,
    selectedRecord,
    setSelectedRecord,
    programId,
    roleKey,
    email,
    changeEmail,
    owner,
    expiresDate,
    setExpiresDate,
    staff,
    setStaff,
    supervisors,
    setSupervisors,
    staffQuery,
    setStaffQuery,
    staffExpanded,
    setStaffExpanded,
    supervisorsExpanded,
    setSupervisorsExpanded,
    reviewOpen,
    setReviewOpen,
    expiryCalendarOpen,
    setExpiryCalendarOpen,
    pendingDelete,
    setPendingDelete,
    pendingStaffRemoval,
    setPendingStaffRemoval,
    removeStaffMutation,
    showUnsavedChangesDialog,
    setShowUnsavedChangesDialog,
    listQuery,
    changeListQuery,
    partnerFilter,
    changePartnerFilter,
    roleFilter,
    changeRoleFilter,
    statusFilter,
    changeStatusFilter,
    selectedProgram,
    selectedRole,
    activeSupervisors,
    supervisorSummary,
    staffSummary,
    activeRoles,
    selectedExpiryDate,
    earliestExpiryDate,
    visibleStaffResults,
    canAddStaffEmail,
    normalizedStaffQuery,
    detailRecord,
    detailProgram,
    detailRole,
    detailOwnerName,
    detailActions,
    detailRelationships,
    detailSupervisorCount,
    detailStaffCount,
    ownerDisplayName,
    reviewExpiryIsCustom,
    reviewExpiryNote,
    startCreate,
    startEdit,
    leaveForm,
    requestLeaveForm,
    openPartnerSettings,
    backFromPartnerSettings,
    applyProgramSettings,
    changeProgram,
    changeRole,
    addStaff,
    addStaffEmail,
    submitFromReview,
    programDefaultsError,
    canSubmit,
    isRelationshipLookupPending,
    preflightMessage,
    filteredOnboardings,
    paginatedOnboardings,
    activeListPage,
    totalListPages,
    showPreviousListPage,
    showNextListPage,
    statusCounts,
  } = usePilotOnboardingAdminController();
  const pendingStaffRemovalName = pendingStaffRemoval
    ? pendingStaffRemoval.advisor.advisorFirstName ||
      firstNameFromEmail(pendingStaffRemoval.advisor.advisorEmail)
    : '';
  const pilotUserName = firstNameFromEmail(email);

  if (programsQuery.isLoading || onboardingsQuery.isLoading) return <LoadingState />;

  if (programsQuery.isError || onboardingsQuery.isError) {
    return (
      <Card className="max-w-3xl border shadow-sm">
        <CardContent className="flex min-h-56 flex-col items-center justify-center gap-3 py-8 text-center">
          <p className="text-foreground font-medium">Pilot onboarding could not be loaded.</p>
          <p className="text-muted-foreground text-sm">
            {getErrorMessage(
              (programsQuery.error ?? onboardingsQuery.error) as Error,
              'Please try again.',
            )}
          </p>
          <Button
            variant="outline"
            onClick={() => {
              programsQuery.refetch();
              onboardingsQuery.refetch();
            }}
          >
            <RefreshCw className="size-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (programs.length === 0) {
    return <EmptyState>No pilot programs are configured.</EmptyState>;
  }

  const detailCanEdit = detailActions?.canEdit ?? false;
  const detailCanDelete = detailActions?.canDelete ?? false;

  return (
    <div className="max-w-[1180px] space-y-4">
      {view === 'program-settings' && selectedProgram ? (
        <PilotPartnerSettings
          key={`${selectedProgram.id}:${selectedProgram.updatedAt}`}
          program={selectedProgram}
          onBack={backFromPartnerSettings}
          onSaved={applyProgramSettings}
        />
      ) : view === 'submitting' && selectedProgram && selectedRole ? (
        <SubmittingInvitationDetails
          program={selectedProgram}
          roleLabel={selectedRole.label}
          email={normalizedEmail}
          supervisorCount={activeSupervisors.length}
          staffCount={staff.length}
          advisorExists={!!currentPreflight?.advisorExists}
          seatExpiresDateTime={dateTimeValue(expiresDate)}
        />
      ) : view === 'form' ? (
        <>
          <div className="flex items-center justify-between gap-4">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <button
                      type="button"
                      onClick={requestLeaveForm}
                      className="text-primary inline-flex items-center gap-1"
                    >
                      <ArrowLeft className="size-4" />
                      Pilot invitations
                    </button>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator>/</BreadcrumbSeparator>
                <BreadcrumbItem>
                  <BreadcrumbPage>
                    {editing ? 'Edit pilot invitation' : 'Onboard pilot user'}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
              {editing && <h2 className="sr-only">Edit pilot invitation</h2>}
            </Breadcrumb>
            {!editing && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                title="Edit partner settings"
                aria-label="Edit partner settings"
                onClick={openPartnerSettings}
              >
                <Pencil className="size-4" />
              </Button>
            )}
          </div>

          <div className="flex flex-wrap items-stretch gap-4">
            <Card className="min-w-0 flex-[1_1_340px] rounded-[14px] border shadow-sm">
              <CardContent className="grid content-start gap-6 p-6">
                <section className="grid gap-1.5">
                  <SectionHeading
                    number="1"
                    title="Pilot partner"
                  />
                  <Select
                    value={programId}
                    onValueChange={changeProgram}
                  >
                    <SelectTrigger
                      aria-label="Pilot partner"
                      size="sm"
                      className="w-full bg-white shadow-sm"
                    >
                      <SelectValue placeholder="Select pilot partner" />
                    </SelectTrigger>
                    <SelectContent>
                      {programs
                        .filter(program => program.isActive)
                        .map(program => (
                          <SelectItem
                            key={program.id}
                            value={program.id}
                          >
                            {program.displayName}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </section>

                <section className="grid gap-1.5">
                  <SectionHeading
                    number="2"
                    title="Role"
                  />
                  <Select
                    value={roleKey}
                    onValueChange={changeRole}
                  >
                    <SelectTrigger
                      aria-label="Role"
                      size="sm"
                      className="w-full bg-white shadow-sm"
                    >
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeRoles.map(role => (
                        <SelectItem
                          key={role.key}
                          value={role.key}
                        >
                          {role.label} ({role.shortLabel})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </section>

                <section className="grid gap-1.5">
                  <SectionHeading
                    number="3"
                    title="Email"
                  />
                  <Input
                    aria-label="Pilot user email"
                    type="email"
                    value={email}
                    onChange={event => changeEmail(event.target.value)}
                    placeholder="name@example.com"
                    className="h-9 rounded-lg shadow-sm"
                  />
                  {preflightMessage.text && (
                    <p className={`text-xs leading-4 ${preflightMessage.className}`}>
                      {preflightMessage.text}
                    </p>
                  )}
                </section>
              </CardContent>
            </Card>

            <Card className="min-w-0 flex-[1.4_1_480px] rounded-[14px] border shadow-sm">
              <CardContent className="flex h-full flex-col gap-5 p-6">
                <Collapsible
                  open={staffExpanded}
                  onOpenChange={setStaffExpanded}
                  className="grid gap-2.5"
                >
                  <div className="flex min-h-7 items-center gap-1">
                    <CollapsibleTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-auto min-h-7 min-w-0 flex-1 justify-start gap-2 px-0 py-0 text-left hover:bg-transparent"
                      >
                        <SectionNumber>4</SectionNumber>
                        <span className="text-foreground text-sm font-semibold">
                          Staff who work with this user
                        </span>
                        <span className="text-muted-foreground text-xs">Optional</span>
                        <span className="min-w-0 flex-1" />
                        {!staffExpanded && (
                          <span className="text-muted-foreground max-w-80 truncate text-[13px]">
                            {staffSummary}
                          </span>
                        )}
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-7 text-gray-500"
                        aria-label={staffExpanded ? 'Collapse staff' : 'Expand staff'}
                      >
                        <ChevronDown
                          className={`size-4 transition-transform ${staffExpanded ? 'rotate-180' : ''}`}
                        />
                      </Button>
                    </CollapsibleTrigger>
                  </div>

                  <CollapsibleContent className="grid gap-2.5">
                    {isRelationshipLookupPending && (
                      <p className="text-muted-foreground flex items-center gap-2 text-xs">
                        <Spinner className="size-3.5" /> Searching existing relationships for this
                        user in the system…
                      </p>
                    )}
                    <InputGroup className="h-9 bg-white">
                      <InputGroupAddon>
                        <Search />
                      </InputGroupAddon>
                      <InputGroupInput
                        aria-label="Search staff"
                        value={staffQuery}
                        onChange={event => setStaffQuery(event.target.value)}
                        placeholder="Search by name, or type an email"
                      />
                    </InputGroup>
                    {staffSearchQuery.isFetching && (
                      <p className="text-muted-foreground flex items-center gap-2 text-xs">
                        <Spinner className="size-3.5" /> Searching…
                      </p>
                    )}
                    {(visibleStaffResults.length > 0 || canAddStaffEmail) && (
                      <ItemGroup className="max-h-52 overflow-y-auto rounded-lg border shadow-sm">
                        {visibleStaffResults.map(advisor => (
                          <Item
                            asChild
                            key={advisor.advisorId}
                            size="sm"
                            className="w-full flex-nowrap rounded-none border-b text-left last:border-b-0"
                          >
                            <button
                              type="button"
                              onClick={() => addStaff(advisor)}
                            >
                              <ItemContent className="min-w-0 items-start gap-0.5 text-left">
                                <ItemTitle className="w-full truncate text-left">
                                  {advisorName(advisor)}
                                </ItemTitle>
                                <ItemDescription className="w-full truncate text-nowrap text-left text-xs">
                                  {advisor.advisorEmail}
                                </ItemDescription>
                              </ItemContent>
                              <Plus className="size-4 shrink-0" />
                            </button>
                          </Item>
                        ))}
                        {canAddStaffEmail && (
                          <Item
                            asChild
                            size="sm"
                            className="w-full flex-nowrap rounded-none border-b text-left last:border-b-0"
                          >
                            <button
                              type="button"
                              onClick={addStaffEmail}
                            >
                              <Plus className="text-primary size-4 shrink-0" />
                              <ItemContent className="min-w-0 items-start gap-0.5 text-left">
                                <ItemTitle className="text-primary w-full truncate text-left">
                                  Add {normalizedStaffQuery}
                                </ItemTitle>
                                <ItemDescription className="w-full truncate text-nowrap text-left text-xs">
                                  Not in Waterlily yet. Access applies when they sign up.
                                </ItemDescription>
                              </ItemContent>
                            </button>
                          </Item>
                        )}
                      </ItemGroup>
                    )}
                    {!!staffQuery.trim() &&
                      !staffSearchQuery.isFetching &&
                      visibleStaffResults.length === 0 &&
                      !canAddStaffEmail && (
                        <p className="text-muted-foreground text-[13px]">
                          No matches. Type a full email to add someone new.
                        </p>
                      )}
                    {staff.length === 0 ? (
                      <p className="text-muted-foreground pl-0.5 text-[13px]">
                        No staff added. Search above to add someone.
                      </p>
                    ) : (
                      <ItemGroup className="overflow-hidden rounded-lg border">
                        {staff.map((item, index) => (
                          <div key={item.advisor.advisorEmail}>
                            {index > 0 && <ItemSeparator />}
                            <Item size="sm">
                              <ItemContent className="min-w-0">
                                <ItemTitle className="truncate text-[13px]">
                                  {advisorName(item.advisor)}
                                </ItemTitle>
                                <ItemDescription className="truncate text-xs">
                                  {item.advisor.advisorId
                                    ? item.advisor.advisorEmail
                                    : 'Not in Waterlily yet'}
                                </ItemDescription>
                              </ItemContent>
                              <ItemActions>
                                <AccessSelect
                                  ariaLabel={`Access for ${item.advisor.advisorEmail}`}
                                  value={item.accessType}
                                  relatedUserName={advisorName(item.advisor)}
                                  onChange={accessType =>
                                    setStaff(current =>
                                      current.map(candidate =>
                                        candidate.advisor.advisorEmail === item.advisor.advisorEmail
                                          ? { ...candidate, accessType }
                                          : candidate,
                                      ),
                                    )
                                  }
                                />
                                <Button
                                  type="button"
                                  size="icon"
                                  variant="ghost"
                                  aria-label={`Remove ${item.advisor.advisorEmail}`}
                                  className="size-9 text-gray-500 hover:bg-red-50 hover:text-red-600"
                                  onClick={() => {
                                    removeStaffMutation.reset();
                                    setPendingStaffRemoval(item);
                                  }}
                                >
                                  <Trash2 className="size-4" />
                                </Button>
                              </ItemActions>
                            </Item>
                          </div>
                        ))}
                      </ItemGroup>
                    )}
                  </CollapsibleContent>
                </Collapsible>

                <Separator />

                <Collapsible
                  open={supervisorsExpanded}
                  onOpenChange={setSupervisorsExpanded}
                  className="grid gap-2"
                >
                  <div className="flex min-h-7 items-center gap-1">
                    <CollapsibleTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-auto min-h-7 min-w-0 flex-1 justify-start gap-2 px-0 py-0 text-left hover:bg-transparent"
                      >
                        <SectionNumber>5</SectionNumber>
                        <span className="text-foreground text-sm font-semibold">Supervisors</span>
                        <span className="min-w-0 flex-1" />
                        {!supervisorsExpanded && (
                          <span className="text-muted-foreground max-w-[360px] truncate text-[13px]">
                            {supervisorSummary}
                          </span>
                        )}
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-7 text-gray-500"
                        aria-label={
                          supervisorsExpanded ? 'Collapse supervisors' : 'Expand supervisors'
                        }
                      >
                        <ChevronDown
                          className={`size-4 transition-transform ${supervisorsExpanded ? 'rotate-180' : ''}`}
                        />
                      </Button>
                    </CollapsibleTrigger>
                  </div>
                  <CollapsibleContent>
                    {activeSupervisors.length ? (
                      <ItemGroup className="overflow-hidden rounded-lg border">
                        {activeSupervisors.map((supervisor, index) => (
                          <div key={supervisor.advisor.advisorEmail}>
                            {index > 0 && <ItemSeparator />}
                            <Item size="sm">
                              <ItemContent className="min-w-0">
                                <ItemTitle className="truncate text-[13px]">
                                  {advisorName(supervisor.advisor)}
                                </ItemTitle>
                                <ItemDescription className="truncate text-xs">
                                  {supervisor.advisor.advisorEmail}
                                </ItemDescription>
                              </ItemContent>
                              <ItemActions>
                                <AccessSelect
                                  ariaLabel={`Access for supervisor ${supervisor.advisor.advisorEmail}`}
                                  value={supervisor.accessType}
                                  relatedUserName={advisorName(supervisor.advisor)}
                                  onChange={accessType =>
                                    setSupervisors(current =>
                                      current.map(candidate =>
                                        candidate.advisor.advisorEmail ===
                                        supervisor.advisor.advisorEmail
                                          ? { ...candidate, accessType }
                                          : candidate,
                                      ),
                                    )
                                  }
                                />
                              </ItemActions>
                            </Item>
                          </div>
                        ))}
                      </ItemGroup>
                    ) : (
                      <p className="text-muted-foreground rounded-lg border border-dashed p-4 text-sm">
                        No supervisors.
                      </p>
                    )}
                  </CollapsibleContent>
                </Collapsible>
              </CardContent>
            </Card>
          </div>

          <Card className="rounded-[14px] border shadow-sm">
            <CardContent className="flex flex-wrap items-end gap-8 px-6 py-5">
              <div className="grid min-w-0 gap-2">
                <SectionHeading
                  number="6"
                  title="Subscription owner"
                />
                <div className="flex h-9 items-center gap-2 pl-7">
                  <span className="text-foreground whitespace-nowrap text-sm font-medium">
                    {ownerDisplayName}
                  </span>
                  {owner?.ownsActiveSubscription && (
                    <span className="text-muted-foreground whitespace-nowrap text-[13px]">
                      · Active subscription
                    </span>
                  )}
                  <Badge variant="success">
                    {editing ? 'Existing invitation' : 'New invitation'}
                  </Badge>
                </div>
              </div>
              <Separator
                orientation="vertical"
                className="hidden self-stretch lg:block"
              />
              <div className="grid gap-2">
                <Label
                  htmlFor="pilot-seat-expiry"
                  className="text-foreground flex min-h-7 items-center text-sm font-semibold"
                >
                  Seat expires
                </Label>
                <div className="flex h-9 items-center">
                  <Popover
                    open={expiryCalendarOpen}
                    onOpenChange={setExpiryCalendarOpen}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        id="pilot-seat-expiry"
                        type="button"
                        variant="outline"
                        aria-label={`Seat expires: ${expiresDate ? formatDate(dateTimeValue(expiresDate)) : 'Pick a date'}`}
                        className="h-9 justify-start gap-2 rounded-lg font-normal"
                      >
                        <CalendarIcon className="size-4" />
                        {expiresDate ? formatDate(dateTimeValue(expiresDate)) : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0"
                      align="start"
                    >
                      <Calendar
                        mode="single"
                        selected={selectedExpiryDate}
                        defaultMonth={selectedExpiryDate ?? earliestExpiryDate}
                        disabled={{ before: earliestExpiryDate }}
                        onSelect={date => {
                          if (!date) return;
                          setExpiresDate(toDateOnlyString(date));
                          setExpiryCalendarOpen(false);
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <span className="min-w-4 flex-1" />
              <div className="grid justify-items-end gap-1.5">
                {!canSubmit && (programDefaultsError || validEmail) && (
                  <span
                    className={`text-xs ${programDefaultsError ? 'text-red-600' : 'text-muted-foreground'}`}
                  >
                    {programDefaultsError ?? 'Complete the required setup to continue.'}
                  </span>
                )}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={requestLeaveForm}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    disabled={!canSubmit}
                    onClick={() => setReviewOpen(true)}
                  >
                    {editing ? 'Review changes' : 'Review and send'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {submitMutation.isError && (
            <p className="text-sm text-red-600">
              {getErrorMessage(
                submitMutation.error as Error,
                'Pilot onboarding could not be saved.',
              )}
            </p>
          )}
        </>
      ) : view === 'details' && detailRecord ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Breadcrumb className="min-w-0">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <button
                      type="button"
                      onClick={() => {
                        setView('list');
                        setSelectedRecord(null);
                      }}
                      className="text-primary inline-flex shrink-0 items-center gap-1"
                    >
                      <ArrowLeft className="size-4" />
                      Pilot invitations
                    </button>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator>/</BreadcrumbSeparator>
                <BreadcrumbItem className="min-w-0">
                  <BreadcrumbPage className="truncate">{detailRecord.email}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            {(detailCanEdit || detailCanDelete) && (
              <div className="flex items-center gap-2">
                {detailCanEdit && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => startEdit(detailRecord)}
                  >
                    <Pencil className="size-3.5" />
                    Edit invitation
                  </Button>
                )}
                {detailCanDelete && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                    onClick={() => setPendingDelete(detailRecord)}
                  >
                    <Trash2 className="size-3.5" />
                    Delete
                  </Button>
                )}
              </div>
            )}
          </div>
          <Card className="rounded-[14px] border shadow-sm">
            <CardContent className="p-6">
              <div className="grid gap-1.5">
                <p className="text-muted-foreground text-xs">
                  {detailProgram?.displayName ?? 'Pilot'} pilot ·{' '}
                  {detailRole?.label ?? 'Legacy user'}
                </p>
                <h2 className="text-foreground break-all text-lg font-semibold">
                  {detailRecord.email}
                </h2>
                <div className="mt-0.5 flex flex-wrap gap-2">
                  <StatusBadge record={detailRecord} />
                </div>
              </div>

              <div className="mt-5 divide-y divide-gray-100 border-t border-gray-100">
                {(
                  [
                    {
                      key: 'flags',
                      title: `Apply ${detailProgram?.shortName ?? 'pilot'} pre-registered feature flags`,
                      status: detailRecord.steps?.flags.status ?? 'succeeded',
                      text:
                        detailRecord.steps?.flags.status === 'failed'
                          ? 'Feature flags could not be applied.'
                          : detailRecord.advisorId
                            ? 'Feature flags applied.'
                            : 'Pre-registered. Applied at signup.',
                    },
                    {
                      key: 'relationships',
                      title: 'Team access',
                      status: detailRecord.steps?.relationships.status ?? 'succeeded',
                      text:
                        detailRecord.steps?.relationships.status === 'failed'
                          ? 'Team access could not be applied.'
                          : `${detailSupervisorCount} supervisors · ${detailStaffCount} team ${detailStaffCount === 1 ? 'member' : 'members'}. Active once both sides have accounts.`,
                    },
                    {
                      key: 'seat_invitation',
                      title: 'Sponsored-seat invitation',
                      status: detailRecord.steps?.seat_invitation.status ?? 'succeeded',
                      text:
                        detailRecord.steps?.seat_invitation.status === 'failed'
                          ? 'The invitation could not be sent.'
                          : `Sent from ${detailOwnerName}.`,
                    },
                    {
                      key: 'account_created',
                      title: 'Account created',
                      status: detailRecord.advisorId ? 'succeeded' : 'pending',
                      text: detailRecord.advisorId
                        ? 'Signed up.'
                        : 'Waiting for the user to sign up.',
                    },
                    {
                      key: 'seat_accepted',
                      title: 'Seat accepted',
                      status:
                        detailRecord.status === 'active'
                          ? 'succeeded'
                          : ['declined', 'expired'].includes(detailRecord.status)
                            ? 'failed'
                            : 'pending',
                      text:
                        detailRecord.status === 'active'
                          ? 'Seat active.'
                          : detailRecord.status === 'declined'
                            ? 'The invitation was declined.'
                            : detailRecord.status === 'expired'
                              ? 'The invitation expired.'
                              : 'Waiting for the user to accept the seat.',
                    },
                  ] as const
                ).map(step => {
                  const succeeded = step.status === 'succeeded';
                  const failed = step.status === 'failed';
                  return (
                    <div
                      key={step.key}
                      className="flex gap-3.5 py-3.5"
                    >
                      {succeeded ? (
                        <CircleCheck className="mt-0.5 size-5 shrink-0 text-green-600" />
                      ) : failed ? (
                        <CircleAlert className="mt-0.5 size-5 shrink-0 text-red-600" />
                      ) : (
                        <Clock3 className="text-muted-foreground mt-0.5 size-5 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-foreground text-sm font-medium">{step.title}</p>
                        <p
                          className={`mt-0.5 text-[13px] leading-[19px] ${failed ? 'text-red-700' : 'text-muted-foreground'}`}
                        >
                          {step.text}
                        </p>
                        {step.key === 'relationships' && detailRelationships.length > 0 && (
                          <Collapsible className="mt-1.5">
                            <CollapsibleTrigger asChild>
                              <Button
                                type="button"
                                variant="link"
                                className="h-auto gap-1 p-0 text-[13px]"
                              >
                                <ChevronRight className="size-3.5" /> View{' '}
                                {detailRelationships.length} relationships
                              </Button>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <ItemGroup className="mt-2 overflow-hidden rounded-lg border">
                                {detailRelationships.map((relationship, index) => (
                                  <div
                                    key={`${relationship.kind}-${relationship.relatedAdvisorEmail}`}
                                  >
                                    {index > 0 && <ItemSeparator />}
                                    <Item size="sm">
                                      <ItemContent className="min-w-0">
                                        <ItemTitle className="truncate text-[13px]">
                                          {relationship.relatedAdvisorEmail}
                                        </ItemTitle>
                                      </ItemContent>
                                      <ItemActions className="text-muted-foreground text-xs">
                                        {accessLabel(
                                          relationship.accessType,
                                          relationship.relatedAdvisorEmail,
                                        )}
                                      </ItemActions>
                                    </Item>
                                  </div>
                                ))}
                              </ItemGroup>
                            </CollapsibleContent>
                          </Collapsible>
                        )}
                        {step.key === 'seat_invitation' && detailRecord.seatExpiresDateTime && (
                          <p className="text-muted-foreground mt-2 text-[13px]">
                            Seat expires{' '}
                            <span className="text-foreground ml-1 font-medium">
                              {formatDate(detailRecord.seatExpiresDateTime)}
                            </span>
                          </p>
                        )}
                        {failed && detailActions?.canRetry && (
                          <Button
                            size="sm"
                            className="mt-2.5"
                            disabled={retryMutation.isPending}
                            onClick={() => retryMutation.mutate(detailRecord.id)}
                          >
                            Retry
                          </Button>
                        )}
                        {step.key === 'seat_invitation' && detailActions?.canResend && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-2.5"
                            disabled={resendMutation.isPending}
                            onClick={() => resendMutation.mutate(detailRecord.id)}
                          >
                            Resend invitation
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <>
          {view === 'list' ? (
            <>
              <div className="flex flex-wrap items-end justify-between gap-4">
                <h2 className="text-foreground whitespace-nowrap text-[22px] font-semibold leading-7">
                  Pilot invitations
                </h2>
                <Button
                  size="lg"
                  onClick={startCreate}
                >
                  <Plus className="size-5" />
                  Onboard pilot user
                </Button>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Tabs
                  value={statusFilter}
                  onValueChange={value => changeStatusFilter(value as PilotOnboardingStatusFilter)}
                >
                  <TabsList className="h-9 flex-wrap">
                    {(
                      [
                        ['all', 'All'],
                        ['attention', 'Needs attention'],
                        ['waiting', 'Waiting on user'],
                        ['complete', 'Complete'],
                      ] as const
                    ).map(([value, label]) => (
                      <TabsTrigger
                        key={value}
                        value={value}
                        className="h-8 rounded-md px-3 text-[13px]"
                      >
                        {label}{' '}
                        <span className="ml-1 text-xs opacity-70">{statusCounts[value]}</span>
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
                <div className="flex flex-wrap gap-2">
                  <InputGroup className="h-9 w-60 bg-white">
                    <InputGroupAddon>
                      <Search />
                    </InputGroupAddon>
                    <InputGroupInput
                      aria-label="Search pilot users"
                      value={listQuery}
                      onChange={event => changeListQuery(event.target.value)}
                      placeholder="Search by email..."
                    />
                  </InputGroup>
                  <Select
                    value={partnerFilter}
                    onValueChange={changePartnerFilter}
                  >
                    <SelectTrigger
                      aria-label="Filter by partner"
                      size="sm"
                      className="w-44 bg-white"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All partners</SelectItem>
                      {programs.map(program => (
                        <SelectItem
                          key={program.id}
                          value={program.id}
                        >
                          {program.displayName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={roleFilter}
                    onValueChange={changeRoleFilter}
                  >
                    <SelectTrigger
                      aria-label="Filter by role"
                      size="sm"
                      className="w-36 bg-white"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All roles</SelectItem>
                      {programs
                        .filter(program => partnerFilter === 'all' || program.id === partnerFilter)
                        .flatMap(program => program.configuration.roles)
                        .filter(role => role.isActive)
                        .filter(
                          (role, index, roles) =>
                            roles.findIndex(item => item.key === role.key) === index,
                        )
                        .map(role => (
                          <SelectItem
                            key={role.key}
                            value={role.key}
                          >
                            {role.shortLabel}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Card className="gap-0 overflow-hidden rounded-[14px] border py-0 shadow-sm">
                <CardContent className="p-0">
                  <Table className="min-w-[900px] table-fixed">
                    <TableHeader className="bg-gray-50">
                      <TableRow className="hover:bg-transparent">
                        <TableHead className="w-[34%] px-4 font-semibold">User</TableHead>
                        <TableHead className="w-32 font-semibold">Partner</TableHead>
                        <TableHead className="w-16 font-semibold">Role</TableHead>
                        <TableHead className="w-40 font-semibold">Subscription owner</TableHead>
                        <TableHead className="w-44 font-semibold">Status</TableHead>
                        <TableHead className="w-8">
                          <span className="sr-only">Actions</span>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredOnboardings.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            className="text-muted-foreground h-20 text-center"
                          >
                            No pilot users match these filters.
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedOnboardings.map(record => {
                          const program = programs.find(
                            candidate => candidate.id === record.pilotProgramId,
                          );
                          const role = program?.configuration.roles.find(
                            candidate => candidate.key === record.roleKey,
                          );
                          const { canEdit, canDelete, canRetry, canResend } =
                            getPilotOnboardingActions(record);
                          const isBusy =
                            retryMutation.isPending ||
                            resendMutation.isPending ||
                            deleteMutation.isPending;
                          const hasActions = canRetry || canResend || canEdit || canDelete;
                          const associatedStaffCount = (
                            record.requestSnapshot?.relationships ?? []
                          ).filter(relationship => relationship.kind === 'associated_staff').length;
                          return (
                            <TableRow
                              key={record.id}
                              onClick={() => {
                                setSelectedRecord(record);
                                setView('details');
                              }}
                              className="cursor-pointer"
                            >
                              <TableCell className="min-w-0 px-4 py-3">
                                <p className="text-foreground truncate text-sm font-medium">
                                  {record.email}
                                </p>
                                <p className="text-muted-foreground text-xs">
                                  {record.source === 'legacy'
                                    ? 'Legacy pilot user'
                                    : `Invited ${formatInvitedDate(record.createdAt)}${
                                        associatedStaffCount
                                          ? ` · ${associatedStaffCount} team ${
                                              associatedStaffCount === 1 ? 'member' : 'members'
                                            }`
                                          : ''
                                      }`}
                                </p>
                              </TableCell>
                              <TableCell className="text-foreground truncate text-sm">
                                {program?.displayName ?? '—'}
                              </TableCell>
                              <TableCell className="text-foreground text-sm font-medium">
                                {record.source === 'legacy'
                                  ? '—'
                                  : (role?.shortLabel ?? record.roleKey ?? '—')}
                              </TableCell>
                              <TableCell className="text-muted-foreground truncate text-sm">
                                {record.subscriptionOwner
                                  ? advisorName(record.subscriptionOwner)
                                  : record.source === 'legacy'
                                    ? '—'
                                    : role
                                      ? role.defaultSubscriptionOwnerEmail
                                      : '—'}
                              </TableCell>
                              <TableCell>
                                <StatusBadge record={record} />
                              </TableCell>
                              <TableCell className="relative text-right">
                                {hasActions ? (
                                  <div onClick={event => event.stopPropagation()}>
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button
                                          size="icon"
                                          variant="ghost"
                                          aria-label={`Actions for ${record.email}`}
                                          disabled={isBusy}
                                        >
                                          <Ellipsis className="size-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        {canRetry && (
                                          <DropdownMenuItem
                                            onSelect={() => retryMutation.mutate(record.id)}
                                          >
                                            <RotateCcw className="size-4" />
                                            Retry
                                          </DropdownMenuItem>
                                        )}
                                        {canResend && (
                                          <DropdownMenuItem
                                            onSelect={() => resendMutation.mutate(record.id)}
                                          >
                                            <Send className="size-4" />
                                            Resend
                                          </DropdownMenuItem>
                                        )}
                                        {canEdit && (
                                          <DropdownMenuItem onSelect={() => startEdit(record)}>
                                            <Pencil className="size-4" />
                                            Edit invitation
                                          </DropdownMenuItem>
                                        )}
                                        {canDelete && (
                                          <DropdownMenuItem
                                            className="text-destructive focus:text-destructive"
                                            onSelect={() => setPendingDelete(record)}
                                          >
                                            <Trash2 className="size-4" />
                                            Delete
                                          </DropdownMenuItem>
                                        )}
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </div>
                                ) : (
                                  <ChevronRight className="ml-auto size-4 text-gray-400" />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
                {totalListPages > 1 && (
                  <div className="flex items-center justify-between border-t px-4 py-3">
                    <p className="text-muted-foreground text-sm">
                      Page {activeListPage + 1} of {totalListPages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={showPreviousListPage}
                        disabled={activeListPage === 0}
                      >
                        Previous
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={showNextListPage}
                        disabled={activeListPage >= totalListPages - 1}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </Card>

              {(retryMutation.isError || resendMutation.isError || deleteMutation.isError) && (
                <p className="text-sm text-red-600">
                  {getErrorMessage(
                    (retryMutation.error ?? resendMutation.error ?? deleteMutation.error) as Error,
                    'The action could not be completed.',
                  )}
                </p>
              )}
            </>
          ) : null}
        </>
      )}

      <Dialog
        open={reviewOpen}
        onOpenChange={setReviewOpen}
      >
        <DialogContent className="flex max-h-[88vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-[880px]">
          <DialogHeader className="gap-1 px-6 pb-4 pr-14 pt-6">
            <DialogTitle>{editing ? 'Review changes' : 'Review and send'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Confirm the updated pilot setup before saving.'
                : 'Check the setup before the invitation goes out.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto px-6 pb-5">
            {selectedProgram && selectedRole && (
              <>
                <div className="grid overflow-hidden rounded-[10px] border sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)]">
                  <div className="bg-primary/[0.05] flex min-w-0 items-center gap-3 border-b px-4 py-3 sm:border-b-0 sm:border-r">
                    <div className="bg-primary/15 text-primary-dark flex size-9 shrink-0 items-center justify-center rounded-full text-[13px] font-semibold">
                      {reviewEmailInitials(normalizedEmail)}
                    </div>
                    <div className="min-w-0">
                      <div className="text-foreground truncate text-sm font-semibold">
                        {normalizedEmail}
                      </div>
                      <div className="text-muted-foreground mt-0.5 truncate text-xs">
                        {selectedRole.label} ({selectedRole.shortLabel}) ·{' '}
                        {selectedProgram.displayName}
                      </div>
                    </div>
                  </div>
                  <div className="grid min-w-0 content-center border-b px-4 py-3 sm:border-b-0 sm:border-r">
                    <div className="text-muted-foreground text-xs">Subscription owner</div>
                    <div className="text-foreground mt-0.5 truncate text-sm font-medium">
                      {ownerDisplayName}
                    </div>
                    <div className="text-muted-foreground mt-0.5 text-xs">
                      {editing ? 'Existing invitation' : 'New invitation'}
                    </div>
                  </div>
                  <div className="grid min-w-0 content-center px-4 py-3">
                    <div className="text-muted-foreground text-xs">Seat expires</div>
                    <div className="text-foreground mt-0.5 text-sm font-medium">
                      {formatDate(dateTimeValue(expiresDate))}
                    </div>
                    <div
                      className={`mt-0.5 text-xs ${reviewExpiryIsCustom ? 'text-amber-700' : 'text-muted-foreground'}`}
                    >
                      {reviewExpiryNote}
                    </div>
                  </div>
                </div>

                <div className="grid items-start gap-4 md:grid-cols-2">
                  <section className="grid min-w-0 content-start gap-2">
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="text-muted-foreground text-xs font-semibold uppercase tracking-[0.04em]">
                        Staff
                      </h3>
                      {staff.length > 0 && (
                        <span className="text-muted-foreground text-xs">
                          {staff.length} {staff.length === 1 ? 'person' : 'people'}
                        </span>
                      )}
                    </div>
                    {staff.length ? (
                      <ItemGroup className="overflow-hidden rounded-[10px] border">
                        {staff.map((item, index) => (
                          <div key={item.advisor.advisorEmail}>
                            {index > 0 && <ItemSeparator />}
                            <ReviewRelationshipItem
                              advisor={item.advisor}
                              email={item.advisor.advisorEmail}
                              accessType={item.accessType}
                            />
                          </div>
                        ))}
                      </ItemGroup>
                    ) : (
                      <p className="text-muted-foreground rounded-[10px] border border-dashed px-3 py-3 text-[13px]">
                        None added
                      </p>
                    )}
                  </section>

                  <section className="grid min-w-0 content-start gap-2">
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="text-muted-foreground text-xs font-semibold uppercase tracking-[0.04em]">
                        Supervisors
                      </h3>
                      {activeSupervisors.length > 0 && (
                        <span className="text-muted-foreground text-xs">
                          {activeSupervisors.length}{' '}
                          {activeSupervisors.length === 1 ? 'person' : 'people'}
                        </span>
                      )}
                    </div>
                    {activeSupervisors.length ? (
                      <ItemGroup className="overflow-hidden rounded-[10px] border">
                        {activeSupervisors.map((supervisor, index) => (
                          <div key={supervisor.advisor.advisorEmail}>
                            {index > 0 && <ItemSeparator />}
                            <ReviewRelationshipItem
                              advisor={supervisor.advisor}
                              email={supervisor.advisor.advisorEmail}
                              accessType={supervisor.accessType}
                            />
                          </div>
                        ))}
                      </ItemGroup>
                    ) : (
                      <p className="text-muted-foreground rounded-[10px] border border-dashed px-3 py-3 text-[13px]">
                        None
                      </p>
                    )}
                  </section>
                </div>
              </>
            )}
            {submitMutation.isError && (
              <p className="text-sm text-red-600">
                {getErrorMessage(
                  submitMutation.error as Error,
                  'The invitation could not be sent.',
                )}
              </p>
            )}
          </div>
          <DialogFooter className="border-t px-6 py-4">
            <Button
              type="button"
              variant="outline"
              disabled={submitMutation.isPending}
              onClick={() => setReviewOpen(false)}
            >
              Back to edit
            </Button>
            <Button
              type="button"
              disabled={!canSubmit || submitMutation.isPending}
              onClick={submitFromReview}
            >
              {editing && submitMutation.isPending ? <Spinner /> : <Send className="size-4" />}
              {editing ? 'Save changes' : 'Send invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!pendingStaffRemoval}
        onOpenChange={open => {
          if (!open && !removeStaffMutation.isPending) {
            removeStaffMutation.reset();
            setPendingStaffRemoval(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {pendingStaffRemovalName}?</AlertDialogTitle>
            <AlertDialogDescription>
              {pilotUserName} and {pendingStaffRemovalName} won’t be linked, so neither will see the
              other’s clients. You can add {pendingStaffRemovalName} back by searching in step 4
              before sending.
            </AlertDialogDescription>
            {removeStaffMutation.isError && (
              <p className="text-sm text-red-600">
                {getErrorMessage(
                  removeStaffMutation.error as Error,
                  'The relationship could not be removed.',
                )}
              </p>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeStaffMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              disabled={removeStaffMutation.isPending}
              onClick={event => {
                event.preventDefault();
                if (pendingStaffRemoval) removeStaffMutation.mutate(pendingStaffRemoval);
              }}
            >
              {removeStaffMutation.isPending ? 'Removing…' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={open => !open && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this pilot invitation?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete?.advisorId
                ? 'The invitation will be invalidated. Existing feature flags and team access will be preserved because this person already has a Waterlily account.'
                : 'The invitation will be invalidated and the pre-registered flags and relationships created by this onboarding will be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              disabled={deleteMutation.isPending}
              onClick={event => {
                event.preventDefault();
                if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
              }}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete invitation'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <UnsavedChangesDialog
        showWarning={showUnsavedChangesDialog}
        handleCancelClose={() => setShowUnsavedChangesDialog(false)}
        handleConfirmClose={leaveForm}
        confirmButtonTestId="pilot-onboarding-unsaved-changes-confirm"
      />
    </div>
  );
}
